import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, DollarSign, Users, Target, Award, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const caseStudies = [
  {
    id: 1,
    company: "SmartBank Pro",
    industry: "FinTech / Banking",
    logo: "🏦",
    challenge: "Skalierung von 5K auf 50K monatliche Installs bei gleichbleibender Profitabilität",
    solution: "Omnichannel-Strategie mit Fokus auf Paid Social, Apple Search Ads und Premium Publisher",
    results: [
      { metric: "Installs", value: "+850%", description: "Von 5K auf 47K monatlich" },
      { metric: "CPI", value: "-42%", description: "Von CHF 8.50 auf CHF 4.90" },
      { metric: "ROAS", value: "3.8x", description: "Nachhaltig profitabel" },
      { metric: "D7 Retention", value: "+18%", description: "Durch Creative Testing" }
    ],
    duration: "8 Monate",
    services: ["Media Buying", "Creative Strategy", "ASO", "Analytics"],
    testimonial: {
      text: "marKIpilot hat uns geholfen, vom Startup zum Marktführer zu skalieren – profitabel und nachhaltig.",
      author: "Max Weber",
      role: "Head of Growth, SmartBank Pro"
    },
    color: "from-blue-500 to-indigo-500",
    situation: "Digitale Banking-App mit starkem Produkt, aber stagnierendem Wachstum: hohe CPIs über Standard-Kanäle, wenig Attribution-Transparenz und Compliance-Prozesse, die schnelle Kampagnen-Iterationen ausbremsten.",
    approach: [
      { phase: "Monat 1–2", title: "Audit & Fundament", text: "Tracking- und Attributions-Audit, Aufbau eines Compliance-Freigabeprozesses für Creatives, Umstellung auf PPI-Kampagnenstruktur." },
      { phase: "Monat 3–5", title: "Skalierung", text: "KI-gestütztes Creative-Testing mit wöchentlichen Iterationen, Budget-Ausbau auf die performantesten Kanäle." },
      { phase: "Monat 6–8", title: "Effizienz & Qualität", text: "Fokus auf Retention-Kohorten und Budget-Shift auf Kanäle mit dem besten LTV/CAC-Verhältnis." }
    ],
    learnings: [
      "Eine vorab compliance-geprüfte Creative-Bibliothek verkürzte Freigaben von Wochen auf Tage",
      "Drei glasklare Kernbotschaften pro Zielgruppe schlugen die breite Feature-Kommunikation deutlich",
      "Das PPI-Modell machte die Budgetplanung fürs Management planbar und verhandelbar"
    ]
  },
  {
    id: 2,
    company: "FitLife 360",
    industry: "Health & Fitness",
    logo: "💪",
    challenge: "Erschließung neuer Zielgruppen und Senkung der Subscriber Acquisition Costs (SAC)",
    solution: "UGC-Strategie mit 50+ Creators, TikTok-First Approach und dynamisches Creative Testing",
    results: [
      { metric: "Subscribers", value: "+320%", description: "12K auf 50K/Monat" },
      { metric: "SAC", value: "-55%", description: "Von CHF 45 auf CHF 20" },
      { metric: "Trial-to-Paid", value: "+28%", description: "Optimierte Onboarding" },
      { metric: "LTV", value: "+65%", description: "Verbesserte Retention" }
    ],
    duration: "6 Monate",
    services: ["UGC Production", "TikTok Ads", "Influencer Marketing", "CRO"],
    testimonial: {
      text: "Das UGC-Framework von marKIpilot hat unsere Performance komplett transformiert. Best-in-class!",
      author: "Lisa Müller",
      role: "CMO, FitLife 360"
    },
    color: "from-green-500 to-emerald-500",
    situation: "Fitness-App mit solider Nutzerbasis, deren Wachstum an steigenden Subscriber-Kosten scheiterte: klassische Ads erreichten die Zielgruppe nicht mehr glaubwürdig, der Content wirkte austauschbar.",
    approach: [
      { phase: "Monat 1–2", title: "Creator-Setup", text: "Recruiting und Briefing von 50+ Fitness-Creators, Aufbau des UGC-Produktionsprozesses mit klaren Hooks pro Zielgruppe." },
      { phase: "Monat 3–4", title: "TikTok-First Testing", text: "Systematisches Testen von UGC-Varianten auf TikTok, Skalierung der Gewinner auf Meta und YouTube Shorts." },
      { phase: "Monat 5–6", title: "Funnel-Optimierung", text: "Onboarding- und Trial-Strecke überarbeitet, Retention-Kampagnen für bestehende Abonnenten." }
    ],
    learnings: [
      "Authentische Creator-Videos schlugen Studio-Produktionen beim CPA um ein Mehrfaches",
      "Der Hook in den ersten 2 Sekunden entschied über Erfolg oder Misserfolg eines Creatives",
      "Trial-to-Paid-Optimierung brachte mehr Umsatz als jede weitere Skalierung des Ad-Budgets"
    ]
  },
  {
    id: 3,
    company: "DragonQuest Heroes",
    industry: "Gaming / Mobile RPG",
    logo: "🐉",
    challenge: "Hochskalierung für globalen Launch bei kompetitiven Gaming-CPIs",
    solution: "Playables-First Strategie, SKAN-Optimierung und programmatic Media Buying",
    results: [
      { metric: "Installs", value: "2.5M", description: "In den ersten 3 Monaten" },
      { metric: "CPI", value: "CHF 1.80", description: "Unter Industrie-Benchmark" },
      { metric: "D1 Retention", value: "52%", description: "Top 5% der Kategorie" },
      { metric: "ARPU", value: "CHF 4.20", description: "Nach 30 Tagen" }
    ],
    duration: "Pre-Launch + 6 Monate",
    services: ["Playable Ads", "SKAN Setup", "Programmatic", "Creative Testing"],
    testimonial: {
      text: "Die technische Expertise im SKAN-Setup und die Creative-Exzellenz waren entscheidend für unseren Erfolg.",
      author: "Tim Schmidt",
      role: "UA Lead, DragonQuest Studios"
    },
    color: "from-purple-500 to-pink-500",
    situation: "Mobile-RPG vor dem globalen Launch in einem der kompetitivsten Gaming-Märkte: hohe Benchmark-CPIs, iOS-Attribution nur noch über SKAN möglich, kein bestehendes Publisher-Netzwerk.",
    approach: [
      { phase: "Pre-Launch", title: "Soft Launch & Playables", text: "Soft Launch in Testmärkten, Entwicklung und Iteration von Playable Ads auf Basis der Kern-Gameplay-Loop." },
      { phase: "Launch (Monat 1–3)", title: "Globale Skalierung", text: "SKAN-Conversion-Schema optimiert, programmatic Media Buying über mehrere DSPs parallel skaliert." },
      { phase: "Post-Launch", title: "Retention & Monetarisierung", text: "Live-Ops-Events mit UA-Kampagnen synchronisiert, Lookalike-Audiences aus zahlenden Spielern." }
    ],
    learnings: [
      "Playables lieferten die qualifiziertesten Installs – Spieler wussten vorab, was sie erwartet",
      "Ein sauberes SKAN-Setup vor dem Launch war entscheidend für iOS-Skalierung",
      "UA und Live-Ops im Takt: Events verdoppelten die Wirkung der Install-Kampagnen"
    ]
  },
  {
    id: 4,
    company: "ShopFlow",
    industry: "E-Commerce / Shopping",
    logo: "🛍️",
    challenge: "Reaktivierung pausierter Performance Marketing und Profitabilität herstellen",
    solution: "Portfolio-Approach mit Meta Dynamic Ads, Google Shopping und Retargeting-Funnel",
    results: [
      { metric: "Revenue", value: "+480%", description: "Vs. vorheriges Quartal" },
      { metric: "ROAS", value: "4.2x", description: "Nachhaltig über 4x" },
      { metric: "AOV", value: "+32%", description: "Durch Upselling" },
      { metric: "New Customers", value: "+290%", description: "Monatlich" }
    ],
    duration: "4 Monate",
    services: ["Meta Ads", "Google Shopping", "Retargeting", "Email Marketing"],
    testimonial: {
      text: "Wir hatten Performance Marketing schon aufgegeben. marKIpilot hat es profitabel gemacht.",
      author: "Anna Klein",
      role: "Co-Founder, ShopFlow"
    },
    color: "from-orange-500 to-red-500",
    situation: "E-Commerce-App, die ihr Performance Marketing nach unprofitablen Kampagnen komplett pausiert hatte: verbranntes Budget, kein Vertrauen ins Team, aber ein gesundes Produkt mit gutem Sortiment.",
    approach: [
      { phase: "Monat 1", title: "Forensik & Neustart", text: "Analyse der gescheiterten Kampagnen, Feed-Qualität und Produktdaten bereinigt, Ziel-ROAS pro Kategorie definiert." },
      { phase: "Monat 2–3", title: "Portfolio-Aufbau", text: "Meta Dynamic Ads und Google Shopping parallel aufgebaut, Retargeting-Funnel nach Warenkorb-Phasen segmentiert." },
      { phase: "Monat 4", title: "Profitabilisierung", text: "Upselling-Strecken und E-Mail-Flows ergänzt, Budgets strikt nach ROAS-Schwellen gesteuert." }
    ],
    learnings: [
      "Die Ursache war nie der Kanal, sondern der Produkt-Feed – Datenqualität vor Budget",
      "Strikte ROAS-Schwellen pro Kategorie verhinderten den Rückfall in unprofitables Skalieren",
      "Retargeting nach Warenkorb-Phasen brachte den höchsten inkrementellen Umsatz"
    ]
  },
  {
    id: 5,
    company: "MoneyMap",
    industry: "FinTech / Personal Finance",
    logo: "💰",
    challenge: "Premium-Positionierung und Senkung der CAC bei steigender Qualität",
    solution: "Brand-First Approach mit Premium Publishern, Native Ads und PR-Integration",
    results: [
      { metric: "Premium Users", value: "+650%", description: "Hochwertige Zielgruppe" },
      { metric: "CAC", value: "-38%", description: "Bei besserer Qualität" },
      { metric: "Conversion Rate", value: "+45%", description: "Landing Page Optimierung" },
      { metric: "Brand Awareness", value: "+180%", description: "Aided Recall" }
    ],
    duration: "10 Monate",
    services: ["Brand Strategy", "Native Advertising", "PR", "Content Marketing"],
    testimonial: {
      text: "marKIpilot versteht, dass Wachstum mehr als Performance ist. Sie haben unsere Marke aufgebaut.",
      author: "Michael Steiner",
      role: "CEO, MoneyMap"
    },
    color: "from-teal-500 to-cyan-500",
    situation: "Personal-Finance-App im Premium-Segment: Discount-getriebene Akquise zog die falsche Zielgruppe an, hohe Churn-Raten und ein Markenbild, das dem Produktanspruch nicht gerecht wurde.",
    approach: [
      { phase: "Monat 1–3", title: "Brand-Fundament", text: "Positionierung geschärft, Messaging-Framework mit drei Kernargumenten, Landing Pages auf Premium-Anspruch gebracht." },
      { phase: "Monat 4–7", title: "Premium-Reichweite", text: "Native Ads bei Qualitäts-Publishern, PR-Platzierungen in Finanzmedien, Content-Serie zu Vermögensaufbau." },
      { phase: "Monat 8–10", title: "Conversion & Loyalität", text: "Landing-Page-Testing, Onboarding personalisiert, Empfehlungsprogramm für Bestandskunden." }
    ],
    learnings: [
      "Weniger, dafür hochwertigere Kanäle senkten die CAC bei gleichzeitig besserer Nutzerqualität",
      "Brand- und Performance-Marketing verstärkten sich messbar – Aided Recall korrellierte mit Conversion Rate",
      "Ein Premium-Produkt braucht Premium-Umfelder: Publisher-Qualität schlug Reichweite"
    ]
  }
];

const industries = [
  { name: "FinTech", count: 28, icon: "🏦" },
  { name: "Health & Fitness", count: 19, icon: "💪" },
  { name: "Gaming", count: 23, icon: "🎮" },
  { name: "E-Commerce", count: 15, icon: "🛍️" },
  { name: "Education", count: 12, icon: "📚" },
  { name: "Travel", count: 8, icon: "✈️" }
];

export default function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Seo
        title="Case Studies – App-Marketing Erfolgsgeschichten"
        description="Exemplarische Case Studies aus FinTech, Gaming und E-Commerce: Wie Pay-per-Install und KI-Optimierung Installs skalieren und CPI senken."
        path="/case-studies"
      />
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-slate-50 opacity-80" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 80% 20%, rgba(20,184,166,0.08) 0%, transparent 50%)`,
        }} />
        
        <motion.div 
          className="relative max-w-6xl mx-auto text-center"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6"
            variants={fadeInUp}
          >
            <Award className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">Case Studies</span>
          </motion.div>
          
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-slate-900 mb-6"
            variants={fadeInUp}
          >
            Erfolgsgeschichten unserer{' '}
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              Partner
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-slate-600 max-w-3xl mx-auto mb-8"
            variants={fadeInUp}
          >
            Entdecke, wie wir App-Unternehmen dabei geholfen haben, ihre Wachstumsziele zu erreichen – 
            von Startups bis hin zu etablierten Marken.
          </motion.p>
          
          <motion.div 
            className="flex flex-wrap gap-6 justify-center text-left"
            variants={fadeInUp}
          >
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="text-3xl font-bold text-teal-600">100+</div>
              <div className="text-sm text-slate-600">Apps betreut</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="text-3xl font-bold text-teal-600">500M+</div>
              <div className="text-sm text-slate-600">Installs generiert</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="text-3xl font-bold text-teal-600">4.8x</div>
              <div className="text-sm text-slate-600">Ø ROAS</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="text-3xl font-bold text-teal-600">CHF 120M+</div>
              <div className="text-sm text-slate-600">Media Budget managed</div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Branchen-Expertise</h2>
            <p className="text-slate-600">Tiefes Verständnis für verschiedene Industrien</p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {industries.map((industry, i) => (
              <motion.div 
                key={i}
                className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200 hover:border-teal-300 transition-all hover:shadow-md"
                variants={fadeInUp}
              >
                <div className="text-3xl mb-2">{industry.icon}</div>
                <div className="font-semibold text-slate-900 text-sm mb-1">{industry.name}</div>
                <div className="text-xs text-slate-500">{industry.count} Apps</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-16"
          >
            {caseStudies.map((study, index) => (
              <motion.div 
                key={study.id}
                variants={fadeInUp}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`case-study-${study.id}`}
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${study.color} p-8 text-white`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-5xl mb-3">{study.logo}</div>
                      <h3 className="text-2xl font-bold mb-1">{study.company}</h3>
                      <p className="text-white/90 text-sm">{study.industry}</p>
                    </div>
                    <Badge className="bg-white/20 text-white border-white/30">{study.duration}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {study.services.map((service, i) => (
                      <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    {/* Challenge & Solution */}
                    <div>
                      {study.situation && (
                        <>
                          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <Users className="w-4 h-4 text-teal-600" /> Ausgangslage
                          </h4>
                          <p className="text-slate-600 mb-6">{study.situation}</p>
                        </>
                      )}
                      <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4 text-teal-600" /> Challenge
                      </h4>
                      <p className="text-slate-600 mb-6">{study.challenge}</p>
                      
                      <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-teal-600" /> Lösung
                      </h4>
                      <p className="text-slate-600">{study.solution}</p>
                    </div>

                    {/* Results */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-teal-600" /> Ergebnisse
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {study.results.map((result, i) => (
                          <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                            <div className="text-2xl font-bold text-teal-600 mb-1">{result.value}</div>
                            <div className="text-xs font-medium text-slate-700 mb-1">{result.metric}</div>
                            <div className="text-xs text-slate-500">{result.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Vorgehen im Zeitverlauf */}
                  {study.approach && (
                    <div className="mb-8" data-testid={`case-study-approach-${study.id}`}>
                      <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-teal-600" /> Vorgehen im Zeitverlauf
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        {study.approach.map((step, i) => (
                          <div key={i} className="bg-slate-50 rounded-lg p-5 border border-slate-200 relative">
                            <div className="text-xs font-semibold text-teal-600 mb-1">{step.phase}</div>
                            <div className="font-semibold text-slate-900 text-sm mb-2">{step.title}</div>
                            <p className="text-xs text-slate-600 leading-relaxed">{step.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Learnings */}
                  {study.learnings && (
                    <div className="mb-8" data-testid={`case-study-learnings-${study.id}`}>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-teal-600" /> Key Learnings
                      </h4>
                      <ul className="space-y-2">
                        {study.learnings.map((l, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Testimonial */}
                  <div className="bg-teal-50 border-l-4 border-teal-600 rounded-lg p-6">
                    <p className="text-slate-700 italic mb-3">"{study.testimonial.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                        {study.testimonial.author.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{study.testimonial.author}</div>
                        <div className="text-xs text-slate-600">{study.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <p className="text-xs text-slate-400 text-center mt-10 max-w-2xl mx-auto" data-testid="case-studies-disclaimer">
            Hinweis: Die dargestellten Case Studies sind exemplarisch und anonymisiert. Firmennamen wurden geändert bzw. sind fiktiv; Kennzahlen dienen der Veranschaulichung typischer Ergebnisse.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit für deine Erfolgsgeschichte?
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Lass uns gemeinsam die nächste Case Study schreiben – mit deiner App als Hauptdarsteller.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/login">
                <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50" data-testid="cta-start-btn">
                  Kostenlos starten <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="about-btn">
                  Über uns erfahren <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
